import { earliestScheduleStartIso, formatCalendarDate, parseIsoCalendarDate } from "./business-date"

/**
 * Matches lockout_layer.validate_schedule_date_range / validate_not_locked_range messages.
 * Preview/maximums/create put the reasons in `details`; create-payment-intent uses a generic
 * `error` ("Campaign cannot be booked") with the reason in `message`, so prefer the specific text.
 */
export function formatSchedulingApiError(
  body: { error?: string; message?: string; details?: string[] } | null | undefined,
  fallback: string,
): string {
  if (!body) return fallback
  if (Array.isArray(body.details) && body.details.length > 0) {
    return body.details.join(" ")
  }
  return body.message || body.error || fallback
}

/** Backend wording from lockout_layer.validate_schedule_date_range (enforce_lead_time). */
const LEAD_TIME_ERROR = /startdate must be on or after (\d{4}-\d{2}-\d{2}) \(station order lead time\)/

/**
 * The minimum start date (YYYY-MM-DD) named in a backend lead-time rejection, or null.
 * Lets the builder resync its calendar when the station's rule changed after it loaded.
 */
export function leadTimeMinimumFromApiError(
  body: { error?: string; message?: string; details?: string[] } | null | undefined,
): string | null {
  if (!body) return null
  const texts = [...(Array.isArray(body.details) ? body.details : []), body.message, body.error]
  for (const text of texts) {
    const m = typeof text === "string" ? LEAD_TIME_ERROR.exec(text) : null
    if (m) return m[1]
  }
  return null
}

/**
 * Client-side mirror of backend validate_schedule_date_range (business calendar + lockouts).
 * Returns the first error message, or null when valid.
 */
export function validateScheduleDateRangeClient(
  businessTodayIso: string | null,
  lockedDates: ReadonlySet<string> | readonly string[],
  startIso: string,
  endIso: string,
  /** Station order rule minimumStartDate (lead time); enforced by preview/create as start >= it. */
  orderRuleMinimumIso?: string | null,
): string | null {
  if (!businessTodayIso) {
    return "Station schedule dates are still loading. Please wait and try again."
  }

  const locked = lockedDates instanceof Set ? lockedDates : new Set(lockedDates)

  if (startIso <= businessTodayIso) {
    return `startdate must be after the current business day (${businessTodayIso})`
  }
  if (endIso <= businessTodayIso) {
    return `enddate must be after the current business day (${businessTodayIso})`
  }
  const earliest = earliestScheduleStartIso(businessTodayIso, orderRuleMinimumIso)
  if (startIso < earliest) {
    return `startdate must be on or after ${earliest} (station order lead time)`
  }
  if (endIso < startIso) return "enddate must be on or after startdate"

  const cur = parseIsoCalendarDate(startIso)
  const endD = parseIsoCalendarDate(endIso)
  while (cur <= endD) {
    const iso = formatCalendarDate(cur)
    if (locked.has(iso)) {
      return `Station schedule is locked for ${iso}; new orders and edits are not allowed`
    }
    cur.setDate(cur.getDate() + 1)
  }

  return null
}
